import React, { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Grid, MenuItem, SelectChangeEvent, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { format } from "date-fns-tz";

import { Job } from "Models/job";
import {
  FormDescription,
  FormDescriptionWrapper,
  ReportOption,
  ReportOptionText,
  TrimReportOption,
  WrapperForm,
  WrapperInput,
} from "./styles";
import { AutoSuggest, Select } from "Components/Form";
import { JobReportKeys, ReportsKeys } from "Util/queries";
import JobReportService from "Services/job.report.service";
import { ReportChecklist } from "Models/report.checklist";
import { Task } from "Models/task";
import Checkbox from "Components/Form/Checkbox";
import { LoadReportGroupBy, ReportName } from "Util/enums";
import { BannerComponent } from "../Banner";
import RadioButton from "Components/Form/RadioGroup";
import SkeletonComponent from "Components/Generic/Skeleton";
import { ReportList } from "./ReportList";
import { JobStatus } from "Util/enums";

interface GenerateReportFormProps {
  order: Job;
  reportSelected?: ReportChecklist;
  setReportSelected?: (report: ReportChecklist) => void;
  isWarningBannerOpened?: boolean;

  tasks?: Task[];
  setTasks?: (tasks: Task[]) => void;

  reportWithPhotos?: number;
  setReportWithPhotos?: (value: number) => void;

  sortReport?: string;
  setSortReport?: (sortReport: string) => void;

  reportsSelected?: ReportChecklist[];
  setReportsSelected?: (report: ReportChecklist[]) => void;
}

export const GenerateReportForm = (props: GenerateReportFormProps) => {
  const {
    order,
    reportSelected,
    setReportSelected,
    tasks,
    isWarningBannerOpened,
    setTasks,
    reportWithPhotos,
    setReportWithPhotos,
    sortReport,
    setSortReport,
    reportsSelected,
    setReportsSelected: setReportsSelectedList,
  } = props;
  const { t: translate } = useTranslation();

  const { data: reportList, isFetching: isFetchingReportList } = useQuery({
    queryKey: ReportsKeys.getAll([]),
    queryFn: async () =>
      await JobReportService.getAvailableReportsByJobId(order.id!, {
        excludedNames: [ReportName.ORDER_BY_STATUS, ReportName.ORDER_BY_ASSIGN_LEADER],
        isQuote: !!window.location.href.includes("quotes"),
      }).then(({ data }) => data),
    enabled: !!order.id!,
    initialData() {
      return [];
    },
  });

  const { data: taskList, isFetching: isFetchingTaskList } = useQuery({
    queryKey: JobReportKeys.getAll([reportSelected, reportsSelected]),
    queryFn: async () => {
      let report = reportSelected;

      if (
        reportsSelected?.length &&
        reportsSelected?.find(
          (report) => report.name?.toLowerCase() !== ReportName.ORDER_SUMMARY_REPORT?.toLowerCase()
        )
      ) {
        const currentReportList = reportsSelected?.filter(
          (report) => report.name?.toLowerCase() !== ReportName.ORDER_SUMMARY_REPORT?.toLowerCase()
        );
        report = currentReportList[currentReportList?.length - 1];
      }

      const result = await JobReportService.availableTasks({
        checklistId: report?.checklistId,
        jobId: order?.id!,
        reportId: report?.reportId,
      }).then(({ data }) => data);

      return result;
    },
    enabled:
      !!reportSelected &&
      !!order.id! &&
      reportSelected?.name?.toLowerCase() !== ReportName.ORDER_SUMMARY_REPORT?.toLowerCase() &&
      reportSelected?.name?.toLowerCase() !== ReportName.WORK_ORDER.toLowerCase(),
    initialData() {
      return [];
    },
  });

  const reportArr = useMemo(
    () =>
      reportList?.filter(
        (report) => report.name?.toLowerCase() !== ReportName.WORK_ORDER.toLowerCase()
      ),
    [reportList]
  );

  const showTaskTypeSelection = useMemo(() => {
    if (!reportSelected) return false;

    if (
      reportSelected &&
      reportSelected?.name?.toLowerCase() !== ReportName.ORDER_SUMMARY_REPORT?.toLowerCase() &&
      reportSelected?.name !== ReportName.WORK_ORDER &&
      reportSelected?.name !== ReportName.ALL_REPORTS
    )
      return true;

    return !!reportsSelected?.filter(
      (report) => report.name?.toLowerCase() !== ReportName.ORDER_SUMMARY_REPORT?.toLowerCase()
    )?.length;
  }, [reportSelected, reportsSelected]);

  const showSortReportForm = useMemo(() => {
    if (!reportSelected) return false;

    if (reportSelected?.name?.toLowerCase().includes("transshipment report")) return true;

    if (!reportsSelected?.length) return false;

    return !!reportsSelected.find((report) =>
      report.name?.toLowerCase().includes("transshipment report")
    );
  }, [reportSelected, reportsSelected]);

  const showRadioButtonPhotosForm = useMemo(() => {
    if (!reportSelected) return false;

    if (reportSelected?.name?.toLowerCase().includes("inventory")) return true;

    if (!reportsSelected?.length) return false;

    return !!reportsSelected.find((report) => report.name?.toLowerCase().includes("inventory"));
  }, [reportSelected, reportsSelected]);

  const getDescriptionTask = (task: Task) => {
    if (task?.type && task?.startDate)
      return `${task?.type} - ${format(new Date(task?.startDate), "dd/MM/yyyy")}`;

    if (task?.type) return task?.type;

    return "";
  };

  return (
    <WrapperForm>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        direction="row"
        spacing={2}
      >
        <Grid item xs={12} md={8}>
          <SkeletonComponent isLoading={isFetchingReportList}>
            <Select
              fullWidth
              required
              label={translate("reportType")}
              disabled={window.location.href.toLowerCase().includes("analytics")}
              placeholder={translate("selectReportType")}
              value={reportSelected?.name?.toString()}
              onChange={(e: SelectChangeEvent<any>) => {
                if (
                  e.target.value === ReportName.ALL_REPORTS ||
                  e.target.value === ReportName.WORK_ORDER
                ) {
                  setReportsSelectedList?.([]);
                  setReportSelected?.({
                    uuid: e.target.value,
                    name: e.target.value,
                  });
                } else {
                  const report = reportList?.find(
                    (report) => report?.name?.toLowerCase() === e.target.value?.toLowerCase()
                  );
                  if (report) {
                    setReportSelected?.(report);
                  }
                }
              }}
            >
              {!window.location.href.includes("quotes") && (
                <MenuItem key="allReports" value={ReportName.ALL_REPORTS}>
                  <Tooltip title={translate("allReports")} placement="left">
                    <span>{translate("allReports")}</span>
                  </Tooltip>
                </MenuItem>
              )}
              {!window.location.href.includes("quotes") && (
                <MenuItem key="workOrder" value={ReportName.WORK_ORDER}>
                  <Tooltip title={translate("workOrder")} placement="left">
                    <span>{translate("workOrder")}</span>
                  </Tooltip>
                </MenuItem>
              )}
              {reportArr.map((report) => (
                <TrimReportOption
                  key={report.name}
                  disabled={report?.isDisabled}
                  value={report?.name?.toString()}
                >
                  <Tooltip title={report.name!} placement="left">
                    <span>{report.name}</span>
                  </Tooltip>
                </TrimReportOption>
              ))}
            </Select>
          </SkeletonComponent>
        </Grid>
        <Grid item xs={12} md={4}>
          <Select label={translate("reportLanguage")} value={"english"} required disabled>
            <MenuItem value="english">{translate("english")}</MenuItem>
          </Select>
        </Grid>
      </Grid>

      {reportSelected?.name === ReportName.WORK_ORDER && (
        <BannerComponent
          title={translate("workOrder")}
          text={`
            ${translate(
              "allAvailableReportsForThisOrderWillBeGeneratedInOneBatchWhichMayTakeLongerThanUsual"
            )}
            ${translate("pleaseNoteThatGrayedOutReportsAreCurrentlyUnavailbaleForThisOrder")}`}
        />
      )}

      {reportSelected?.name === ReportName.ALL_REPORTS && (
        <BannerComponent
          title={translate("allReports")}
          text={`
          ${translate(
            "youCanChooseTheSpecificReportsYouNeedAndEachSelectedReportWillBeGeneratedAsAnIndividualFileWhichMayTakeLongerThanUsual"
          )}
          ${translate("pleaseNoteThatGrayedOutReportsAreCurrentlyUnavailbaleForThisOrder")}
            `}
        />
      )}

      {reportSelected?.name &&
      [ReportName.WORK_ORDER, ReportName.ALL_REPORTS].includes(
        reportSelected?.name as ReportName
      ) ? (
        <ReportList
          showReportListFooter={reportSelected?.name === ReportName.ALL_REPORTS}
          showCheckboxForEachReport={reportSelected?.name === ReportName.ALL_REPORTS}
          enableReportList={reportArr?.filter((report) => !report.isDisabled)}
          reports={reportArr}
          setSelectedReports={(reports) => setReportsSelectedList?.(reports)}
        />
      ) : null}

      {showTaskTypeSelection && (
        <WrapperInput>
          <Grid container xs={12}>
            <Grid item xs={12}>
              <SkeletonComponent isLoading={isFetchingTaskList}>
                <>
                  <AutoSuggest
                    placeholder={translate("selectTaskReportType")}
                    label={translate("taskReportType")}
                    value={tasks}
                    getOptionKey={(task: Task) => task.id!}
                    onChange={(tasks: Task[]) => {
                      setTasks?.(tasks || []);
                    }}
                    multiple={reportSelected?.name !== ReportName.SURVEY_REPORT}
                    required
                    options={taskList}
                    getOptionSelected={(option: Task, value: Task) => {
                      return option.id === value.id;
                    }}
                    getOptionLabel={(task: Task) => getDescriptionTask(task)}
                    renderOption={(
                      props: React.HTMLAttributes<any>,
                      task: Task,
                      { selected }: { selected: boolean }
                    ) => (
                      <ReportOption {...props}>
                        <Checkbox checked={selected} />
                        <ReportOptionText>{getDescriptionTask(task)}</ReportOptionText>
                      </ReportOption>
                    )}
                  />
                  {reportSelected?.name !== ReportName.SURVEY_REPORT && (
                    <FormDescriptionWrapper>
                      <FormDescription>
                        {translate(
                          "applicableOnInventoryConditionReportMechanicalAndEletricalChecklistHightValuePropertyDemange"
                        )}
                      </FormDescription>
                    </FormDescriptionWrapper>
                  )}
                </>
              </SkeletonComponent>
            </Grid>
          </Grid>
        </WrapperInput>
      )}

      {showSortReportForm && (
        <WrapperInput>
          <Select
            label={translate("sortReportBy")}
            required
            value={sortReport}
            onChange={(e: SelectChangeEvent<any>) => setSortReport?.(e.target.value)}
          >
            <MenuItem value={LoadReportGroupBy.LIST}>{translate("sortedByInventory")}</MenuItem>
            <MenuItem value={LoadReportGroupBy.LOADED_INTO}>
              {translate("sortedAndGroupedByStorageBox")}
            </MenuItem>
          </Select>
          <FormDescriptionWrapper>
            <FormDescription>
              {translate("applicableOnlyOnLoadAndTransshipmentReport")}
            </FormDescription>
          </FormDescriptionWrapper>
        </WrapperInput>
      )}

      {showRadioButtonPhotosForm && (
        <WrapperInput>
          <RadioButton
            options={[
              { key: "withoutPhotos", value: 0, label: translate("withoutPhotos") },
              { key: "withPhotos", value: 1, label: translate("withPhotos") },
            ]}
            label={translate("photos")}
            description={translate("applicableOnlyOnInventoryConditionReport")}
            value={reportWithPhotos}
            onChange={(e) => setReportWithPhotos?.(Number(e.target.value))}
          />
        </WrapperInput>
      )}

      {reportSelected?.name === ReportName.WORK_ORDER && isWarningBannerOpened && (
        <BannerComponent
          variant="warning"
          text={`${translate("generatingYourReportMightTakeLongerThanUsual")} ${translate(
            "weWillNotifyYouAsSoonAsItsReadyForDownload"
          )}`}
        />
      )}
    </WrapperForm>
  );
};
